import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { portalApi, VerifyResponse } from '../services/portalApi';

interface PortalAuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  customer: VerifyResponse['customer'] | null;
  account: VerifyResponse['account'] | null;
  tokenExpiresAt: Date | null;
  error: string | null;
}

interface PortalAuthContextType extends PortalAuthState {
  authenticate: (token: string) => Promise<boolean>;
  logout: () => void;
}

const PortalAuthContext = createContext<PortalAuthContextType | null>(null);

const SESSION_KEY = 'portalSession';

function saveSession(data: { customer: VerifyResponse['customer']; account: VerifyResponse['account']; tokenExpiresAt: string | null }) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch { /* ignore quota errors */ }
}

function loadSession(): { customer: VerifyResponse['customer']; account: VerifyResponse['account']; tokenExpiresAt: Date | null } | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Check if cached expiry is in the past
    if (data.tokenExpiresAt && new Date(data.tokenExpiresAt) < new Date()) return null;
    return {
      customer: data.customer,
      account: data.account,
      tokenExpiresAt: data.tokenExpiresAt ? new Date(data.tokenExpiresAt) : null,
    };
  } catch {
    return null;
  }
}

function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

export const PortalAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authenticationVersion = useRef(0);
  const [state, setState] = useState<PortalAuthState>(() => {
    // Load cached session for instant display
    const incomingToken = new URLSearchParams(window.location.search).get('token');
    const cached = !incomingToken && portalApi.getToken() ? loadSession() : null;
    if (cached) {
      return {
        isLoading: false,
        isAuthenticated: true,
        customer: cached.customer,
        account: cached.account,
        tokenExpiresAt: cached.tokenExpiresAt,
        error: null,
      };
    }
    return {
      isLoading: !incomingToken && !!portalApi.getToken(),
      isAuthenticated: false,
      customer: null,
      account: null,
      tokenExpiresAt: null,
      error: null,
    };
  });

  const authenticate = useCallback(async (token: string): Promise<boolean> => {
    const version = ++authenticationVersion.current;
    setState(prev => ({ ...prev, isLoading: !prev.isAuthenticated, error: null }));

    try {
      const response = await portalApi.verifyToken(token);
      if (version !== authenticationVersion.current) return false;

      if (!response.valid) {
        setState({
          isLoading: false,
          isAuthenticated: false,
          customer: null,
          account: null,
          tokenExpiresAt: null,
          error: response.error || 'Invalid token',
        });
        portalApi.clearToken();
        clearSession();
        return false;
      }

      // Store the token and cache session data
      portalApi.setToken(token);
      saveSession({
        customer: response.customer,
        account: response.account,
        tokenExpiresAt: response.tokenExpiresAt || null,
      });

      setState({
        isLoading: false,
        isAuthenticated: true,
        customer: response.customer || null,
        account: response.account || null,
        tokenExpiresAt: response.tokenExpiresAt ? new Date(response.tokenExpiresAt) : null,
        error: null,
      });

      return true;
    } catch (err: any) {
      if (version !== authenticationVersion.current) return false;
      // Offline fallback is only valid for the token belonging to this session.
      const cached = token === portalApi.getToken() ? loadSession() : null;
      if (cached) {
        setState({
          isLoading: false,
          isAuthenticated: true,
          customer: cached.customer,
          account: cached.account,
          tokenExpiresAt: cached.tokenExpiresAt,
          error: null,
        });
        return true;
      }

      setState({
        isLoading: false,
        isAuthenticated: false,
        customer: null,
        account: null,
        tokenExpiresAt: null,
        error: err.message || 'Failed to authenticate',
      });
      portalApi.clearToken();
      clearSession();
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    authenticationVersion.current += 1;
    portalApi.clearToken();
    clearSession();
    setState({
      isLoading: false,
      isAuthenticated: false,
      customer: null,
      account: null,
      tokenExpiresAt: null,
      error: null,
    });
  }, []);

  // Try to re-verify stored token on mount (background if cached)
  useEffect(() => {
    // The email link owns authentication when present. Rechecking a saved token
    // here can invalidate the new session after the link has already succeeded.
    if (new URLSearchParams(window.location.search).get('token')) return;
    const storedToken = portalApi.getToken();
    if (storedToken) {
      authenticate(storedToken);
    } else {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [authenticate]);

  return (
    <PortalAuthContext.Provider
      value={{
        ...state,
        authenticate,
        logout,
      }}
    >
      {children}
    </PortalAuthContext.Provider>
  );
};

export const usePortalAuth = (): PortalAuthContextType => {
  const context = useContext(PortalAuthContext);
  if (!context) {
    throw new Error('usePortalAuth must be used within a PortalAuthProvider');
  }
  return context;
};
